import { ReportTemplateEntityType, type GetMeasuringPointsQuery } from '@/graphql/types';
import type { MeasuringPointRenderContext, ProjectRenderContext } from './elements/types';
import {
    getPreviewableProjects,
    getPreviewMeasuringPoints,
    requireProjectTimezone,
    type PreviewMeasuringPointModel,
} from './previewContextModels';

export interface PreviewSelection {
    projectId?: string;
    measuringPointId?: string;
}

type PreviewMeasuringPointRenderContext = Omit<MeasuringPointRenderContext, 'measuringPoint'> & {
    measuringPoint: PreviewMeasuringPointModel;
};

export type PreviewRenderContext = ProjectRenderContext | PreviewMeasuringPointRenderContext;

function resolveProjectPreviewContext(
    data: GetMeasuringPointsQuery,
    previewSelection?: PreviewSelection
): ProjectRenderContext {
    const previewProjects = getPreviewableProjects(data);
    const projectsById = new Map(previewProjects.map((project) => [project.id, project]));

    let selectedProject = previewProjects[0];
    if (previewSelection?.projectId) {
        const configuredProject = projectsById.get(previewSelection.projectId);
        if (configuredProject) {
            selectedProject = configuredProject;
        }
    }

    if (!selectedProject) {
        throw new Error('No projects are available for preview.');
    }

    return {
        kind: 'project',
        project: {
            id: selectedProject.id,
            name: selectedProject.name,
            timezone: requireProjectTimezone(selectedProject),
        },
    };
}

function resolveMeasuringPointPreviewContext(
    measuringPoints: readonly PreviewMeasuringPointModel[],
    previewSelection?: PreviewSelection
): PreviewMeasuringPointRenderContext {
    let selectedMeasuringPoint = measuringPoints[0];

    if (previewSelection?.measuringPointId) {
        const configuredMeasuringPoint = measuringPoints.find(
            (measuringPoint) => measuringPoint.id === previewSelection.measuringPointId
        );

        if (configuredMeasuringPoint) {
            selectedMeasuringPoint = configuredMeasuringPoint;
        }
    }

    if (!selectedMeasuringPoint) {
        throw new Error('No measuring points are available for preview.');
    }

    return {
        kind: 'measuring-point',
        measuringPoint: selectedMeasuringPoint,
    };
}

export function resolvePreviewContextFromQueryData(
    entityType: ReportTemplateEntityType,
    data: GetMeasuringPointsQuery,
    previewSelection?: PreviewSelection
): PreviewRenderContext {
    if (entityType === ReportTemplateEntityType.Project) {
        return resolveProjectPreviewContext(data, previewSelection);
    }

    if (entityType === ReportTemplateEntityType.MeasuringPoint) {
        return resolveMeasuringPointPreviewContext(
            getPreviewMeasuringPoints(data),
            previewSelection
        );
    }

    throw new Error(`Cannot render preview for entity type "${String(entityType)}".`);
}
